/**
 * The phone's bottom tab bar (#648, MainPhone.dc.html): the same primary navigation as the desktop header, with the
 * board's stroke icons above the labels. It renders instead of the header nav, never beside it, so there is one
 * primary `<nav>` in the accessibility tree. NavLink writes `aria-current="page"` itself.
 */

import { NavLink } from 'react-router';

export type TabIcon = 'home' | 'homework' | 'progress' | 'prep';

export interface Tab {
  readonly to: string;
  readonly label: string;
  readonly icon: TabIcon;
}

const ICONS: Record<TabIcon, React.JSX.Element> = {
  home: (
    <>
      <path d="M3.5 10.5 12 3.5l8.5 7" />
      <path d="M5.5 9v11.5h13V9" />
      <path d="M10 20.5v-6h4v6" />
    </>
  ),
  homework: (
    <>
      <path d="M14 3.5H6.5a1 1 0 0 0-1 1v15a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V8z" />
      <path d="M14 3.5V8h4.5" />
      <path d="M8.5 12.5h7M8.5 16.5h4.5" />
    </>
  ),
  progress: (
    <>
      <path d="M9 4.5 3.5 6.5v13l5.5-2 6 2 5.5-2v-13l-5.5 2z" />
      <path d="M9 4.5v13M15 6.5v13" />
    </>
  ),
  prep: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </>
  ),
};

export function TabBar({ label, tabs }: { readonly label: string; readonly tabs: readonly Tab[] }): React.JSX.Element {
  return (
    <nav className="tab-bar" aria-label={label}>
      {tabs.map((tab) => (
        // `end` on Home alone (#661): a section's tab stays current on its nested pages, e.g. a homework's feedback.
        <NavLink key={tab.to} to={tab.to} end={tab.to === '/'}>
          <svg
            aria-hidden="true"
            focusable="false"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {ICONS[tab.icon]}
          </svg>
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}
